"use client";

import { cn } from "@/lib/utils";
import type { ThrowballTemplateData } from "../throwball-template";
import {
  ContactShadow,
  CourtNet,
  FieldMatchFrame,
  type FieldMatchState,
  FieldScore,
  FloatingProp,
  PerspectivePlane,
  type PlaneSpec,
  projectOnPlane,
  StadiumStands,
} from "./field-sport-scene-kit";

type ThrowballDisplayPreviewProps = {
  data: ThrowballTemplateData | null;
  participants: {
    id: string;
    displayName: string;
    avatarUrl: string | null;
    username: string | null;
  }[];
};

// Indoor arena: a terracotta synthetic court inside a deep-teal run-off.
const COURT: PlaneSpec = {
  top: 66,
  depth: 176,
  tilt: 58,
  perspective: 320,
  farWidth: 112,
};
const BALL_SPOT = projectOnPlane(COURT, 0.5, 60);

/** Throwball: bigger than a volleyball, smooth white panels with a red band. */
const Throwball = ({ className }: { className?: string }) => (
  <div
    className={cn(
      "relative overflow-hidden rounded-full bg-[radial-gradient(circle_at_34%_28%,#ffffff_0%,#f1ede6_42%,#b9b2a6_100%)]",
      className,
    )}
    style={{
      boxShadow:
        "inset -2px -3px 5px rgba(40,20,10,0.35), inset 1px 2px 3px rgba(255,255,255,0.9)",
    }}
  >
    <div className="absolute top-[38%] right-[-10%] left-[-10%] h-[24%] -rotate-[18deg] bg-[#d6402b]/85" />
    <div className="absolute top-[-20%] bottom-[-20%] left-[-36%] w-[76%] rounded-[100%] border border-black/15" />
    <div className="absolute top-[-20%] right-[-36%] bottom-[-20%] w-[76%] rounded-[100%] border border-black/15" />
    <div className="absolute top-[10%] left-[18%] h-[30%] w-[34%] rounded-full bg-white/70 blur-[1px]" />
  </div>
);

/** The court, flat in plane coordinates. */
function SportCourt() {
  return (
    <>
      {/* Run-off */}
      <div className="absolute inset-0 bg-[linear-gradient(180deg,#123747_0%,#18495d_100%)]" />
      {/* Playing area: polyurethane with a fine embossed grip */}
      <div className="absolute top-[18px] right-[7%] left-[7%] h-[124px] bg-[linear-gradient(90deg,#a9482a_0%,#c75b33_30%,#cf6438_50%,#c75b33_70%,#a9482a_100%)]">
        <div className="absolute inset-0 [background-image:radial-gradient(rgba(0,0,0,0.2)_0.6px,transparent_0.9px),radial-gradient(rgba(255,220,190,0.18)_0.6px,transparent_0.9px)] [background-size:3px_3px,3px_3px] [background-position:0_0,1.5px_1.5px] opacity-50" />
        {/* Boundary, centre line, neutral zone either side of the net */}
        <div className="absolute inset-0 border-2 border-white/90" />
        <div className="absolute top-0 bottom-0 left-1/2 w-[2px] -translate-x-1/2 bg-white/90" />
        <div className="absolute top-0 bottom-0 left-[45%] w-[2px] bg-white/70" />
        <div className="absolute top-0 right-[45%] bottom-0 w-[2px] bg-white/70" />
        {/* Service lines */}
        <div className="absolute top-0 bottom-0 left-[12%] w-[2px] bg-white/55" />
        <div className="absolute top-0 right-[12%] bottom-0 w-[2px] bg-white/55" />
      </div>
    </>
  );
}

function ThrowballArena() {
  return (
    <>
      <div className="absolute inset-0 bg-[linear-gradient(180deg,#07060c_0%,#140c16_100%)]" />

      <StadiumStands
        className="h-[66px]"
        crowdA="255,180,120"
        crowdB="120,220,255"
        crowdOpacity={0.6}
        baseClassName="bg-[linear-gradient(180deg,#06050a_0%,#170e18_100%)]"
        boardClassName="bg-[linear-gradient(90deg,#3b1408_0%,#ff6a3a_20%,#ffe4d4_35%,#2ad1ff_55%,#3b1408_70%,#ff6a3a_86%,#ffe4d4_100%)] opacity-80 shadow-[0_0_14px_rgba(255,110,60,0.55)]"
      />

      {/* Overhead LED panels along the roof truss */}
      <div className="absolute inset-x-[6%] top-[3px] flex justify-between">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className={cn(
              "h-[4px] w-[16px] rounded-[1px] bg-[#f4fbff] shadow-[0_0_6px_2px_rgba(210,240,255,0.7),0_0_16px_6px_rgba(210,240,255,0.25)]",
              (i === 2 || i === 3) && "invisible",
            )}
          />
        ))}
      </div>
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(220,240,255,0.12)_0%,transparent_45%)]" />

      <PerspectivePlane plane={COURT}>
        <SportCourt />
      </PerspectivePlane>

      {/* Glossy floor: light streaks under the panels, a central key light */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_6%_36%_at_18%_70%,rgba(230,246,255,0.26),transparent),radial-gradient(ellipse_6%_36%_at_82%_70%,rgba(230,246,255,0.26),transparent),radial-gradient(ellipse_40%_30%_at_50%_60%,rgba(255,236,220,0.14),transparent)]" />
      <div className="absolute inset-x-0 top-[60px] h-[22px] bg-[linear-gradient(180deg,transparent,rgba(200,236,255,0.14)_50%,transparent)]" />

      <CourtNet
        plane={COURT}
        farV={10}
        nearV={150}
        height={24}
        drop={11}
        postClassName="fill-[#c9d2da]"
        meshColor="rgba(255,255,255,0.6)"
      />

      <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_62%,rgba(10,4,12,0.45)_100%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_95%_at_50%_45%,transparent_55%,rgba(0,0,0,0.5)_100%)]" />
    </>
  );
}

export function ThrowballDisplayPreview({
  data,
  participants,
}: ThrowballDisplayPreviewProps) {
  const toSide = (team: "your-team" | "opponent-team") =>
    participants
      .filter((participant) => data?.positions?.[participant.id] === team)
      .map((participant) => ({
        ...participant,
        handle: participant.username ?? undefined,
      }));
  const yourTeam = toSide("your-team");
  const oppTeam = toSide("opponent-team");

  const showScore = !!data && data.hasScore !== false;
  const yourWon =
    !!data && data.matchScore.yourSets > data.matchScore.opponentSets;
  const opponentWon =
    !!data && data.matchScore.opponentSets > data.matchScore.yourSets;

  const matchState: FieldMatchState =
    yourTeam.length === 0 && oppTeam.length === 0
      ? "EMPTY"
      : showScore
        ? "FULL"
        : "NO_SCORE";

  const totalSets = data
    ? data.matchScore.yourSets + data.matchScore.opponentSets
    : 0;

  return (
    <FieldMatchFrame
      label="Throwball"
      frameClassName="bg-[#0d0910]"
      matchState={matchState}
      scene={<ThrowballArena />}
      yourTeam={yourTeam}
      opponentTeam={oppTeam}
      yourWinner={showScore && yourWon}
      opponentWinner={showScore && opponentWon}
      linkToProfiles
      bandClassName="top-[52px] bottom-1"
      decor={
        matchState !== "NO_SCORE" ? null : (
          // Mid-throw: the ball sails over the net.
          <div
            className="absolute -translate-x-1/2"
            style={{ left: BALL_SPOT.left, top: BALL_SPOT.top - 8 }}
          >
            <ContactShadow className="top-[48px] left-1/2 h-[6px] w-[20px] -translate-x-1/2 opacity-70" />
            <FloatingProp amplitude={4} duration={4}>
              <Throwball className="h-[22px] w-[22px] @sm:h-[26px] @sm:w-[26px]" />
            </FloatingProp>
          </div>
        )
      }
      center={
        matchState === "FULL" && data ? (
          <>
            <FieldScore
              transitionKey={totalSets}
              left={data.matchScore.yourSets}
              right={data.matchScore.opponentSets}
              leftWinner={yourWon}
              rightWinner={opponentWon}
            />
            {data.sets && data.sets.length > 0 ? (
              <div className="flex flex-wrap justify-center gap-1">
                {data.sets
                  .map((set, n) => ({ ...set, id: `set-${n + 1}` }))
                  .slice(-5)
                  .map((set) => {
                    const yourSetWon = set.yourScore > set.opponentScore;
                    return (
                      <span
                        key={set.id}
                        className={cn(
                          "rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wide tabular-nums ring-1 @sm:text-[11px]",
                          yourSetWon
                            ? "bg-black/50 text-white ring-white/20"
                            : "bg-black/35 text-white/65 ring-white/10",
                        )}
                      >
                        {set.yourScore}-{set.opponentScore}
                      </span>
                    );
                  })}
              </div>
            ) : null}
          </>
        ) : null
      }
    />
  );
}
